import axios from "axios";

/**
 * Get backend base URL based on current hostname
 * For localhost testing with subdomains, automatically detects the hostname
 * and uses the same hostname with port 5100 for the backend
 */
const getBackendBaseUrl = (): string => {
  // If REACT_APP_BACKEND_BASE_URL is explicitly set, use it
  if (process.env.REACT_APP_BACKEND_BASE_URL && process.env.REACT_APP_BACKEND_BASE_URL !== "/") {
    return process.env.REACT_APP_BACKEND_BASE_URL;
  }

  // For localhost subdomain testing, use current hostname with backend port
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;

    // If using subdomain.localhost, use same hostname with backend port
    if (hostname.includes("localhost") || hostname.includes("local")) {
      return `http://${hostname}:5100`;
    }

    // For production/staging, use same hostname with backend port
    return `${window.location.protocol}//${hostname}`;
  }

  // Fallback for SSR or when window is not available
  return "http://localhost:5100";
};

const BACKEND_BASE_URL = getBackendBaseUrl();

// Platform API client with platform token
export const platformApi = axios.create({
  baseURL: `${BACKEND_BASE_URL === "/" ? "" : BACKEND_BASE_URL}/api`,
  withCredentials: true,
});

platformApi.interceptors.request.use(
  (config) => {
    const platformToken = localStorage.getItem("platformToken");
    if (platformToken) {
      config.headers.Authorization = `Bearer ${platformToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Platform API methods
export const platformApiService = {
  // Stores
  getStores: (params?: any) => platformApi.get("/platform/stores", { params }),
  getStore: (storeId: string) => platformApi.get(`/platform/stores/${storeId}`),
  createStore: (data: any) => platformApi.post("/platform/stores", data),
  updateStore: (storeId: string, data: any) => platformApi.put(`/platform/stores/${storeId}`, data),
  deleteStore: (storeId: string) => platformApi.delete(`/platform/stores/${storeId}`),
  toggleStoreStatus: (storeId: string, isActive: boolean) =>
    platformApi.patch(`/platform/stores/${storeId}/status`, { isActive }),
  approveStore: (storeId: string, action: "approve" | "reject", adminEmail?: string, adminName?: string) =>
    platformApi.post(`/platform/stores/${storeId}/approve`, { action, adminEmail, adminName }),
  getStoreAnalytics: (storeId: string, params?: any) =>
    platformApi.get(`/platform/stores/${storeId}/analytics`, { params }),
  getStoreNginxStatus: (storeId: string) =>
    platformApi.get(`/platform/stores/${storeId}/nginx-status`),
  sendStoreAdminPasswordReset: (storeId: string, email: string) =>
    platformApi.post(`/platform/stores/${storeId}/send-password-reset`, { email }),

  // Subscriptions
  getSubscriptions: () => platformApi.get("/platform/subscriptions"),
  manageStoreSubscription: (storeId: string, action: string, data?: any) =>
    platformApi.post(`/platform/subscriptions/${storeId}/manage`, { action, ...data }),
  generatePaymentLink: (storeId: string, returnUrl?: string) =>
    platformApi.post(`/platform/subscriptions/${storeId}/payment-link`, { returnUrl }),
  activateSubscription: (storeId: string) =>
    platformApi.post(`/platform/subscriptions/${storeId}/activate`),
  expireSubscription: (storeId: string) =>
    platformApi.post(`/platform/subscriptions/${storeId}/expire`),
  cancelScheduledChanges: (storeId: string) =>
    platformApi.post(`/platform/subscriptions/${storeId}/cancel-scheduled`),

  // Subscription Requests
  getPendingSubscriptionRequests: () => platformApi.get("/platform/subscription-requests"),
  getSubscriptionRequest: (requestId: string) => platformApi.get(`/platform/subscription-requests/${requestId}`),
  approveSubscriptionRequest: (requestId: string, adminNotes?: string) =>
    platformApi.post(`/platform/subscription-requests/${requestId}/approve`, { adminNotes }),
  rejectSubscriptionRequest: (requestId: string, adminNotes?: string) =>
    platformApi.post(`/platform/subscription-requests/${requestId}/reject`, { adminNotes }),

  // Invoices
  getInvoices: (params?: any) => platformApi.get("/platform/invoices", { params }),
  getInvoice: (invoiceId: string) => platformApi.get(`/platform/invoices/${invoiceId}`),

  // Audit Logs
  getAuditLogs: (params?: any) => platformApi.get("/platform/audit-logs", { params }),

  // Breaches
  getBreaches: (params?: { hours?: number; limit?: number; offset?: number }) => {
    const queryParams = new URLSearchParams();
    if (params?.hours) queryParams.append("hours", params.hours.toString());
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.offset) queryParams.append("offset", params.offset.toString());
    return platformApi.get(`/platform/breaches?${queryParams.toString()}`);
  },
  getBreachStats: (params?: { hours?: number }) => {
    const queryParams = new URLSearchParams();
    if (params?.hours) queryParams.append("hours", params.hours.toString());
    return platformApi.get(`/platform/breaches/stats?${queryParams.toString()}`);
  },
  getBreachById: (breachId: string) => platformApi.get(`/platform/breaches/${breachId}`),
  getBreachLogs: (breachId: string, params?: { limit?: number }) => {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    return platformApi.get(`/platform/breaches/${breachId}/logs?${queryParams.toString()}`);
  },
  generateBreachReport: (breachId: string) => platformApi.post(`/platform/breaches/${breachId}/report`),
  notifyAffectedUsers: (breachId: string) => platformApi.post(`/platform/breaches/${breachId}/notify`),

  // Plugins
  getPlugins: () => platformApi.get("/platform/plugins"),
  upsertPlugin: (data: any) => platformApi.post("/platform/plugins", data),
  updatePlugin: (pluginId: string, data: any) => platformApi.put(`/platform/plugins/${pluginId}`, data),
  getPluginUsage: (pluginId: string) => platformApi.get(`/platform/plugins/${pluginId}/usage`),

  // Plans
  getPlans: () => platformApi.get("/platform/plans"),
  upsertPlan: (data: any) => platformApi.post("/platform/plans", data),
  updatePlan: (planId: string, data: any) => platformApi.put(`/platform/plans/${planId}`, data),
  getPlanUsage: (planId: string) => platformApi.get(`/platform/plans/${planId}/usage`),

  // Platform Pages
  getPlatformPages: () => platformApi.get("/platform/pages"),
  getPlatformPage: (slug: string) => platformApi.get(`/platform/pages/${slug}`),
  upsertPlatformPage: (data: any) => platformApi.post("/platform/pages", data),
  updatePlatformPage: (slug: string, data: any) => platformApi.put(`/platform/pages/${slug}`, data),
  deletePlatformPage: (pageId: string) => platformApi.delete(`/platform/pages/${pageId}`),

  // Dashboard
  getDashboardStats: (params?: any) => platformApi.get("/platform/dashboard", { params }),

  // File Upload
  uploadImage: (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    return platformApi.post("/platform/upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },

  // Payouts
  getPayouts: (params?: any) => platformApi.get("/platform/payouts", { params }),
  getPayout: (payoutId: string) => platformApi.get(`/platform/payouts/${payoutId}`),
  createPayout: (data: any) => platformApi.post("/platform/payouts", data),
  processPayout: (payoutId: string) => platformApi.post(`/platform/payouts/${payoutId}/process`),
  cancelPayout: (payoutId: string) => platformApi.post(`/platform/payouts/${payoutId}/cancel`),
  getStorePayoutSummary: (storeId: string) =>
    platformApi.get(`/platform/payouts/stores/${storeId}/summary`),
  getAvailableFunds: (storeId: string) =>
    platformApi.get(`/platform/payouts/stores/${storeId}/available`),
  updateStorePayoutSettings: (storeId: string, data: any) =>
    platformApi.put(`/platform/payouts/stores/${storeId}/settings`, data),

  // Income
  getPlatformIncome: (params?: any) => platformApi.get("/platform/income", { params }),
  getIncomeStats: () => platformApi.get("/platform/income/stats"),
  getStoreContributions: (startDate?: Date, endDate?: Date) => {
    const params: any = {};
    if (startDate) params.startDate = startDate.toISOString();
    if (endDate) params.endDate = endDate.toISOString();
    return platformApi.get("/platform/income/contributions", { params });
  },
  exportIncomeReport: (startDate?: Date, endDate?: Date, format: "csv" | "json" = "csv") => {
    const params: any = { format };
    if (startDate) params.startDate = startDate.toISOString();
    if (endDate) params.endDate = endDate.toISOString();
    return platformApi.get("/platform/income/export", { params, responseType: format === "csv" ? "text" : "json" });
  },

  // Store Products
  getStoreProducts: (storeId: string, params?: any) =>
    platformApi.get(`/platform/stores/${storeId}/products`, { params }),
  getStoreProduct: (storeId: string, productId: string) =>
    platformApi.get(`/platform/stores/${storeId}/products/${productId}`),
  createStoreProduct: (storeId: string, data: any) =>
    platformApi.post(`/platform/stores/${storeId}/products`, data),
  updateStoreProduct: (storeId: string, productId: string, data: any) =>
    platformApi.patch(`/platform/stores/${storeId}/products/${productId}`, data),
  deleteStoreProduct: (storeId: string, productId: string) =>
    platformApi.delete(`/platform/stores/${storeId}/products/${productId}`),
  batchDeleteStoreProducts: (storeId: string, ids: string[]) =>
    platformApi.post(`/platform/stores/${storeId}/products/batch-delete`, { ids }),
  bulkUpdateStoreProductStatus: (storeId: string, ids: string[], status: string) =>
    platformApi.post(`/platform/stores/${storeId}/products/bulk-status`, { ids, status }),

  // Contact Messages
  getContactMessages: (params?: { page?: number; limit?: number; isRead?: boolean }) => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.isRead !== undefined) queryParams.append("isRead", params.isRead.toString());
    return platformApi.get(`/platform/contact-messages?${queryParams.toString()}`);
  },
  getContactMessage: (messageId: string) => platformApi.get(`/platform/contact-messages/${messageId}`),
  markContactMessageAsRead: (messageId: string) => platformApi.patch(`/platform/contact-messages/${messageId}/read`),
  deleteContactMessage: (messageId: string) => platformApi.delete(`/platform/contact-messages/${messageId}`),

  // Platform Zones
  ensurePlatformPageZones: (slug: string, zones: string[]) =>
    platformApi.post(`/platform/pages/${slug}/ensure-zones`, { zones }),
  getPlatformZone: (slug: string, zoneName: string) =>
    platformApi.get(`/platform/pages/${slug}/zones/${zoneName}`),
  updatePlatformZone: (slug: string, zoneName: string, zoneContent: any) =>
    platformApi.put(`/platform/pages/${slug}/zones/${zoneName}`, { zone: zoneContent }),
  deletePlatformZone: (slug: string, zoneName: string) =>
    platformApi.delete(`/platform/pages/${slug}/zones/${zoneName}`),
  getDefaultZoneTemplate: (slug: string, zoneName: string) =>
    platformApi.get(`/platform/pages/${slug}/zones/${zoneName}/default`),
  resetAllZonesForPage: (slug: string) =>
    platformApi.post(`/platform/pages/${slug}/zones/reset`),
};

